import { useMemo } from 'react';
import { Plus, CheckCircle2, AlertTriangle } from 'lucide-react';
import { FIELD_CLS, LABEL_CLS } from '../../styles/tokens';
import { READING_FIELDS, isStabilized, type Reading } from '../../lib/stabilization';
import * as haptics from '../../lib/haptics';

interface Props {
  readings: Reading[];
  setReadings: (next: Reading[]) => void;
}

/** Field-parameter capture. Tech adds one reading per stabilization
 *  interval; we run isStabilized() after each new reading and show a
 *  green ✓ banner when 3 consecutive readings fall within EPA Region 6
 *  tolerances. */
export function FieldParamsStep({ readings, setReadings }: Props) {
  const latest = readings[readings.length - 1] ?? {} as Reading;
  const stab = useMemo(() => isStabilized(readings), [readings]);

  function setLatestField(key: keyof Omit<Reading, 'recorded_at'>, raw: string) {
    const parsed = raw.trim() === '' ? null : Number(raw);
    if (raw.trim() !== '' && !Number.isFinite(parsed)) return;
    if (readings.length === 0) {
      setReadings([{ [key]: parsed, recorded_at: Date.now() } as Reading]);
      return;
    }
    const next = [...readings];
    next[next.length - 1] = { ...next[next.length - 1], [key]: parsed as number | null };
    setReadings(next);
  }

  function addReading() {
    haptics.tap();
    setReadings([...readings, { recorded_at: Date.now() }]);
  }

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-mono-100">Field parameters</h2>
        <p className="text-xs text-mono-400 mt-0.5">
          Capture readings until three consecutive measurements stabilize (EPA Region 6 criteria).
        </p>
      </header>

      {/* Stabilization status banner */}
      {readings.length >= 1 && (
        <div className={`rounded-lg border p-3 flex items-center gap-2 ${stab.stabilized ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-amber-500/30 bg-amber-500/5 text-amber-200'}`}>
          {stab.stabilized ? <CheckCircle2 className="h-5 w-5 shrink-0" strokeWidth={2} /> : <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={2} />}
          <div className="text-sm">
            {stab.stabilized
              ? `Stabilized · ${stab.consecutive} consecutive readings within tolerance.`
              : stab.failingFields.length > 0
                ? `Not stabilized — ${stab.failingFields.join(', ')} still drifting.`
                : `${readings.length === 1 ? 'Add a second reading' : `${stab.consecutive} of 3 consecutive`} to stabilize.`}
          </div>
        </div>
      )}

      {/* Current reading grid */}
      <div className="grid grid-cols-2 gap-3">
        {READING_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className={LABEL_CLS}>
              {f.label} <span className="text-mono-600 normal-case tracking-normal">({f.unit})</span>
            </span>
            <input
              type="text"
              inputMode="decimal"
              step={f.step}
              className={`${FIELD_CLS} mt-1`}
              value={latest[f.key] != null ? String(latest[f.key]) : ''}
              onChange={(e) => setLatestField(f.key, e.target.value)}
              placeholder={f.tolerance === '—' ? '—' : f.tolerance}
            />
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-mono-500">
          {readings.length === 0 ? 'No readings yet' : `${readings.length} reading${readings.length === 1 ? '' : 's'} captured`}
        </span>
        <button
          type="button"
          onClick={addReading}
          className="inline-flex items-center gap-1.5 min-h-touch px-3 py-1.5 rounded-md border border-mono-700 hover:border-cyan-500/60 active:bg-mono-800 text-mono-200 text-sm transition-colors"
        >
          <Plus className="h-4 w-4" strokeWidth={2.4} />
          {readings.length === 0 ? 'Add first reading' : 'Add next reading'}
        </button>
      </div>

      {/* History list */}
      {readings.length > 0 && (
        <details className="text-xs">
          <summary className="text-mono-400 cursor-pointer select-none py-1">
            Reading history ({readings.length})
          </summary>
          <ul className="mt-2 space-y-1 text-mono-300">
            {readings.map((r, i) => (
              <li key={r.recorded_at} className="flex flex-wrap gap-x-2 gap-y-0.5 py-1 border-b border-mono-800/60 last:border-0">
                <span className="text-mono-500 w-8">#{i + 1}</span>
                {READING_FIELDS.map((f) => r[f.key] != null && (
                  <span key={f.key}>
                    <span className="text-mono-500">{f.label}:</span> {String(r[f.key])} {f.unit}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
